/** HTML de impressão de receituário — dados do GET `/clinic/prescriptions/:id/print` (mesmo que a pré-visualização em Receituários). */
import {
  CLINIC_DOC_SIGNATURE_CSS,
  buildClinicLetterheadBlockHtml,
  buildClinicMultiSheetPrintHtml,
  buildClinicPrintDocumentHtml,
  buildLetterheadFooterUnitsHtml,
  buildLetterheadTopSpacerHtml,
  isClinicLetterheadEnabled,
  type ClinicPrintSheetSpec,
} from './clinicPrintLetterhead';
import { renderClinicalBodyForPrint } from './clinicalHtml';
import {
  prescriptionTypeLabel,
  prescriptionTypeColors,
  resolveEffectivePrescriptionType,
} from './prescriptionTypes';
import { formatPrescriptionItemQuantity } from './prescriptionItemQuantity';
import { formatPrintActiveIngredient } from './prescriptionActiveIngredient';
import { prescriptionHasContinuousUse } from './prescriptionContinuousUse';
import {
  formatMonthCompetence,
  getMultiMonthCount,
  getMultiMonthStep,
  shiftPrescriptionDates,
} from './prescriptionMultiMonth';
import { getHideIssueDateOnPrint } from './prescriptionPrintSettings';
import { inlineDocumentFontSize, scaleDocumentPrintFontSizes } from './clinicDocumentPrintFont';
import { resolvePrescriptionHeaderText } from './prescriptionHeaderText';
import {
  getPrescriptionViaLabels,
  splitPrescriptionByViaRequirement,
} from './prescriptionVia';

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDate(d: string | undefined | null): string {
  if (!d) return '-';
  const dt = new Date(String(d).split('T')[0] + 'T12:00:00');
  return dt.toLocaleDateString('pt-BR');
}

function nl2br(s: unknown): string {
  return esc(s).replace(/\n/g, '<br/>');
}

const PRESCRIPTION_EXTRA_CSS_RAW = `
  .prx-title { text-align: center; font-size: 15px; font-weight: 700; color: var(--lh-accent); margin: 4px 0 6px; text-transform: uppercase; letter-spacing: 1px; }
  .prx-type-badge { text-align: center; margin-bottom: 10px; }
  .prx-type-badge span { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 11px; font-weight: 600; }
  .prx-number { text-align: center; font-size: 10px; color: #9ca3af; margin-bottom: 14px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; }
  .info-item { font-size: 11px; line-height: 1.4; }
  .info-label { font-weight: 600; color: #374151; }
  .info-value { color: #4b5563; }
  .medication-item { margin-bottom: 12px; padding: 10px; border: 1px solid #e5e7eb; border-radius: 6px; page-break-inside: avoid; }
  .medication-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 6px; }
  .medication-name { font-weight: 700; font-size: 13px; color: #111; }
  .medication-active-ingredient { font-size: 10px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.02em; margin-top: 1px; line-height: 1.25; }
  .medication-number { font-size: 10px; color: #9ca3af; font-weight: 600; }
  .medication-details { font-size: 11px; color: #4b5563; line-height: 1.6; }
  .medication-instructions { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #e5e7eb; font-size: 10px; color: #6b7280; }
  .controlled-badge { display: inline-block; padding: 1px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; color: #fff; background: #ef4444; margin-left: 8px; }
  .prx-validity { text-align: left; margin-top: 24px; margin-bottom: 0; font-size: 10px; color: #6b7280; }
  .prx-footer-note { text-align: center; margin-top: 8px; font-size: 9px; color: #9ca3af; }
  .prx-via-info { text-align: center; margin-top: 20px; padding-top: 10px; border-top: 1px dashed #d1d5db; font-size: 9px; color: #9ca3af; font-style: italic; }
  .prx-header-text { text-align: center; font-size: 11px; color: #4b5563; margin: 0 0 6px; }
  .clinical-body ul, .clinical-body ol { margin: 0.35em 0 0.35em 1.4em; padding: 0; }
  .clinical-body li { margin: 0.15em 0; }
  .prx-memed-note { margin: 0 0 14px; padding: 8px 10px; border-left: 3px solid var(--lh-accent); background: rgba(var(--lh-accent-rgb), 0.06); font-size: 10px; line-height: 1.55; color: #374151; }
  .prx-memed-access { margin-top: 20px; padding: 12px; border: 1px solid rgba(var(--lh-accent-rgb), 0.25); border-radius: 6px; background: rgba(var(--lh-accent-rgb), 0.04); page-break-inside: avoid; }
  .prx-memed-access-title { font-size: 11px; font-weight: 700; color: var(--lh-accent); text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 6px; }
  .prx-memed-access-row { font-size: 11px; color: #374151; line-height: 1.7; word-break: break-all; }
  .prx-memed-code { display: inline-block; padding: 1px 8px; border-radius: 4px; background: var(--lh-accent); color: #fff; font-family: 'SFMono-Regular', Consolas, monospace; font-size: 13px; font-weight: 700; letter-spacing: 2px; }
  .prx-memed-signature { margin-top: 24px; text-align: center; page-break-inside: avoid; }
  .prx-memed-signature-name { font-size: 12px; font-weight: 700; color: #111; }
  .prx-memed-signature-caption { font-size: 10px; color: #6b7280; margin: 1px 0 0; }
  .prx-memed-signature-seal { display: inline-block; margin-top: 6px; padding: 3px 10px; border-radius: 12px; background: rgba(var(--lh-accent-rgb), 0.10); color: var(--lh-accent); font-size: 10px; font-weight: 700; }
  .prx-memed-sandbox { margin: 0 0 12px; padding: 6px 10px; border: 1px dashed #f59e0b; border-radius: 4px; background: #fffbeb; text-align: center; font-size: 10px; font-weight: 700; color: #b45309; text-transform: uppercase; letter-spacing: 0.5px; }
  .prx-continuous-use { text-align: center; margin: 12px 0; padding: 6px; background: rgba(var(--lh-accent-rgb), 0.06); border: 1px solid rgba(var(--lh-accent-rgb), 0.25); border-radius: 4px; font-size: 11px; font-weight: 600; color: var(--lh-accent); }
  .prx-month-page { page-break-after: always; }
  .prx-month-page:last-child { page-break-after: auto; }
  .prx-sheet-portrait { max-width: 180mm; margin: 0 auto; }
  .prx-via-banner { text-align: center; font-size: 11px; font-weight: 700; color: var(--lh-accent); margin-bottom: 10px; padding: 6px 12px; background: rgba(var(--lh-accent-rgb), 0.08); border: 2px solid rgba(var(--lh-accent-rgb), 0.35); border-radius: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
  .prx-month-banner { text-align: center; font-size: 11px; font-weight: 600; color: var(--lh-accent); margin-bottom: 10px; padding: 5px 10px; background: rgba(var(--lh-accent-rgb), 0.08); border: 1px solid rgba(var(--lh-accent-rgb), 0.22); border-radius: 4px; text-transform: uppercase; letter-spacing: 0.4px; }
  .prx-page-counter { text-align: right; margin: -4px 0 6px; font-size: 9px; font-weight: 600; color: #64748b; }
  .prx-print-table { width: 100%; border-collapse: collapse; border-spacing: 0; }
  .prx-print-table thead { display: table-header-group; }
  .prx-print-table tfoot { display: table-footer-group; }
  .prx-print-table tbody { display: table-row-group; }
  .prx-print-table td { vertical-align: top; padding: 0; border: none; }
  .prx-print-head .clinic-sheet-letterhead { margin-bottom: 14px; }
  .prx-print-foot .lh-footer-units { margin-top: 0; }
  @media print {
    .prx-print-table thead { display: table-header-group; }
    .prx-print-table tfoot { display: table-footer-group; }
    .prx-print-head { padding-top: 1mm; }
    .prx-print-head .clinic-sheet-letterhead {
      padding-top: 2mm;
      margin-top: 0;
    }
    .sheet.sheet--page-break .prx-print-head {
      padding-top: 3mm;
    }
    .sheet.sheet--page-break .prx-print-head .clinic-sheet-letterhead {
      padding-top: 3mm;
    }
    /* A impressão precisa ser mais compacta que a pré-visualização. Sem isso,
       uma receita com 6–8 itens deixa poucos milímetros no fim da folha e o
       bloco indivisível de assinatura é empurrado sozinho para a página 2. */
    .prx-print-body .section { margin-bottom: 6px !important; }
    .prx-print-body .section-title {
      margin-bottom: 5px !important;
      padding-bottom: 3px !important;
    }
    .prx-print-body .medication-item {
      margin-bottom: 5px !important;
      padding: 6px 8px !important;
    }
    .prx-print-body .medication-header { margin-bottom: 2px !important; }
    .prx-print-body .medication-details { line-height: 1.35 !important; }
    .prx-print-body .medication-instructions {
      margin-top: 2px !important;
      padding-top: 2px !important;
      line-height: 1.35 !important;
    }
    .prx-print-body .prx-validity { margin-top: 6px !important; }
    .prx-print-body .clinic-doc-sign-zone {
      margin-top: 8px !important;
      padding-top: 3px !important;
    }
    .prx-print-body .clinic-doc-sign-spacer { min-height: 12px !important; }
    .prx-print-body .clinic-doc-sign-spacer--with-img { min-height: 4px !important; }
    .prx-print-body .clinic-doc-sign-prof-img-wrap img { max-height: 42px !important; }
    .prx-print-table .lh-footer-units {
      margin-top: 0 !important;
      padding-top: 8px !important;
    }
  }
`;

const PRESCRIPTION_EXTRA_CSS =
  scaleDocumentPrintFontSizes(PRESCRIPTION_EXTRA_CSS_RAW) + CLINIC_DOC_SIGNATURE_CSS;

/** Lista de medicamentos/itens — compartilhada pelo receituário próprio e pela receita Memed. */
function buildMedicationsHtml(items: any[], startIndex = 0): string {
  return items
    .map((item: any, idx: number) => {
      const namePart = `${startIndex + idx + 1}) ${esc(item.medication_name)}${item.concentration ? ` ${esc(item.concentration)}` : ''}${item.pharmaceutical_form ? ` - ${esc(item.pharmaceutical_form)}` : ''}`;
      const controlled = item.is_controlled ? '<span class="controlled-badge">CONTROLADO</span>' : '';
      const posology = [
        item.dosage,
        item.frequency,
        item.route ? `via ${item.route}` : '',
        item.duration ? `por ${item.duration}` : '',
      ].filter(Boolean).map(esc).join(', ');
      const quantityLabel = formatPrescriptionItemQuantity(item);
      const activeIngredient = formatPrintActiveIngredient(item);
      return `<div class="medication-item">
      <div class="medication-header">
        <div>
          <div class="medication-name">${namePart} ${controlled}</div>
          ${activeIngredient ? `<div class="medication-active-ingredient">${esc(activeIngredient)}</div>` : ''}
        </div>
        ${quantityLabel ? `<div class="medication-number">${esc(quantityLabel)}</div>` : ''}
      </div>
      ${posology ? `<div class="medication-details">
        <strong>Posologia:</strong> ${posology}
      </div>` : ''}
      ${item.special_instructions ? `<div class="medication-instructions"><strong>Atenção:</strong> ${esc(item.special_instructions)}</div>` : ''}
    </div>`;
    })
    .join('');
}

type PrescriptionItemPage = {
  items: any[];
  startIndex: number;
};

/**
 * Divide uma prescrição estruturada em folhas físicas completas. Sete itens
 * curtos cabem no layout compacto A4; textos longos consomem parte adicional
 * do orçamento para que a assinatura e o rodapé permaneçam na mesma folha.
 */
export function paginatePrescriptionItems(items: any[]): PrescriptionItemPage[] {
  if (!items.length) return [{ items: [], startIndex: 0 }];

  const PAGE_CAPACITY = 7;
  const pages: PrescriptionItemPage[] = [];
  let current: any[] = [];
  let currentWeight = 0;
  let startIndex = 0;

  const weightOf = (item: any): number => {
    const heading = [
      item?.medication_name,
      item?.concentration,
      item?.pharmaceutical_form,
      item?.quantity,
      formatPrintActiveIngredient(item),
    ].filter(Boolean).join(' ');
    const details = [
      item?.dosage,
      item?.frequency,
      item?.route,
      item?.duration,
    ].filter(Boolean).join(' ');
    const attention = String(item?.special_instructions || '');

    return 1
      + Math.max(0, Math.ceil((heading.length - 90) / 90)) * 0.35
      + Math.max(0, Math.ceil((details.length - 100) / 100)) * 0.3
      + Math.ceil(attention.length / 120) * 0.45;
  };

  items.forEach((item, index) => {
    const itemWeight = Math.min(PAGE_CAPACITY, weightOf(item));
    if (current.length && currentWeight + itemWeight > PAGE_CAPACITY) {
      pages.push({ items: current, startIndex });
      current = [];
      currentWeight = 0;
      startIndex = index;
    }
    current.push(item);
    currentWeight += itemWeight;
  });

  if (current.length) pages.push({ items: current, startIndex });
  return pages;
}

function buildPrescriptionHeaderBlockHtml(clinic: any): string {
  // Com timbrado, nome/especialidade/CRM/RQE ficam só no cabeçalho ao lado da logo.
  if (isClinicLetterheadEnabled(clinic)) return '';
  const text = resolvePrescriptionHeaderText(clinic);
  return text ? `<div class="prx-header-text">${nl2br(text)}</div>` : '';
}

function buildPatientSectionHtml(pr: any): string {
  return `<div class="section">
  <div class="section-title">Dados do Paciente</div>
  <div class="info-grid">
    <div class="info-item"><span class="info-label">Nome: </span><span class="info-value">${esc(pr.patient_name)}</span></div>
    ${pr.patient_cpf ? `<div class="info-item"><span class="info-label">CPF: </span><span class="info-value">${esc(pr.patient_cpf)}</span></div>` : ''}
    ${pr.patient_age_text ? `<div class="info-item"><span class="info-label">Idade: </span><span class="info-value">${esc(pr.patient_age_text)}</span></div>` : ''}
    ${pr.patient_address ? `<div class="info-item"><span class="info-label">Endereço: </span><span class="info-value">${esc(pr.patient_address)}</span></div>` : ''}
  </div>
</div>`;
}

function buildClinicalInfoSectionHtml(pr: any): string {
  if (!pr.diagnosis && !pr.clinical_indication) return '';
  return `<div class="section">
  <div class="section-title">Informações Clínicas</div>
  ${pr.diagnosis ? `<div class="info-item"><span class="info-label">Diagnóstico: </span><span class="info-value">${esc(pr.diagnosis)}${pr.diagnosis_code ? ` (CID: ${esc(pr.diagnosis_code)})` : ''}</span></div>` : ''}
  ${pr.clinical_indication ? `<div class="info-item"><span class="info-label">Indicação: </span><span class="info-value">${esc(pr.clinical_indication)}</span></div>` : ''}
</div>`;
}

function buildProfessionalCredentialLines(pr: any, clinic: any): string {
  const credentials = Array.isArray(pr.professional_credentials)
    ? pr.professional_credentials
        .filter(Boolean)
        .map((c: string) => `<p class="prx-memed-signature-caption" style="font-style:italic;color:#374151;">${esc(c)}</p>`)
        .join('')
    : '';
  const registration =
    pr.professional_registration || clinic.responsible_registration
      ? `<p class="prx-memed-signature-caption">${esc(pr.professional_registration_type || clinic.responsible_registration_type || 'CRM')} ${esc(pr.professional_registration || clinic.responsible_registration)}${pr.professional_registration_state ? ` - ${esc(pr.professional_registration_state)}` : ''}</p>`
      : '';
  return `${credentials}${registration}`;
}

/**
 * Receita emitida na Memed. O original com validade legal é o PDF assinado em
 * ICP-Brasil hospedado pela Memed, então esta folha é a via do prontuário: traz
 * o que foi prescrito e como o paciente acessa a receita digital — sem linha de
 * assinatura manual, que induziria a tratá-la como o documento oficial.
 */
function buildMemedPrescriptionPrintHtml(pr: any, clinic: any): string {
  const items = Array.isArray(pr.items) ? pr.items : [];
  const link = String(pr.memed_digital_link || '').trim();
  const unlockCode = String(pr.memed_unlock_code || '').trim();
  const isSandbox = String(pr.memed_environment || '').trim() === 'integration';
  const parsedIssuedAt = pr.memed_issued_at ? new Date(pr.memed_issued_at) : null;
  const issuedAt =
    parsedIssuedAt && !Number.isNaN(parsedIssuedAt.getTime()) ? parsedIssuedAt : null;
  const signedAtText = issuedAt ? issuedAt.toLocaleString('pt-BR') : '';
  const issuedTimeText = issuedAt
    ? issuedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : '';

  const accessBlock = link
    ? `<div class="prx-memed-access">
  <div class="prx-memed-access-title">Receita digital do paciente</div>
  <div class="prx-memed-access-row">${esc(link)}</div>
  ${unlockCode ? `<div class="prx-memed-access-row" style="margin-top:6px">Código de desbloqueio: <span class="prx-memed-code">${esc(unlockCode)}</span></div>` : ''}
  <div class="prx-memed-access-row" style="margin-top:6px;color:#6b7280;font-size:${inlineDocumentFontSize(10)}">
    A farmácia dispensa o medicamento pelo endereço acima. O original assinado é o PDF da Memed.
  </div>
</div>`
    : `<div class="prx-memed-access">
  <div class="prx-memed-access-title">Receita digital do paciente</div>
  <div class="prx-memed-access-row" style="color:#6b7280;font-size:${inlineDocumentFontSize(10)}">
    Link ainda não publicado pela Memed. Abra a receita em Receituários para obter o PDF assinado.
  </div>
</div>`;

  const bodyHtml = `
<style>${PRESCRIPTION_EXTRA_CSS}</style>
${isSandbox ? '<div class="prx-memed-sandbox">Ambiente de homologação Memed — documento sem validade legal</div>' : ''}
${buildPrescriptionHeaderBlockHtml(clinic)}
<div class="prx-title">Receita digital Memed</div>
<div class="prx-type-badge">
  <span style="background-color: rgba(var(--lh-accent-rgb), 0.12); color: var(--lh-accent);">${esc(prescriptionTypeLabel(pr.prescription_type || 'simple'))}</span>
</div>
<div class="prx-number">
  Nº ${esc(pr.prescription_number)} | Emissão: ${formatDate(pr.issue_date)}${issuedTimeText ? ` às ${esc(issuedTimeText)}` : ''}${pr.validity_date ? ` | Validade: ${formatDate(pr.validity_date)}` : ''}
</div>
<div class="prx-memed-note">
  Via para o prontuário. O documento com validade legal é a receita digital assinada
  eletronicamente na Memed, acessível pelo endereço no fim desta folha.
</div>
${buildPatientSectionHtml(pr)}
${buildClinicalInfoSectionHtml(pr)}
<div class="section">
  <div class="section-title">Prescrição</div>
  ${buildMedicationsHtml(items) || `<p style="font-size:${inlineDocumentFontSize(11)};color:#9ca3af">(sem itens)</p>`}
</div>
${pr.notes ? `<div class="section"><div class="section-title">Observações</div><p style="font-size: ${inlineDocumentFontSize(11)}; line-height: 1.6;">${esc(pr.notes)}</p></div>` : ''}
${prescriptionHasContinuousUse(pr) ? '<div class="prx-continuous-use">USO CONTÍNUO</div>' : ''}
${accessBlock}
<div class="prx-memed-signature">
  <p class="prx-memed-signature-name">${esc(pr.professional_name || clinic.responsible_name || 'Profissional Responsável')}</p>
  ${buildProfessionalCredentialLines(pr, clinic)}
  <span class="prx-memed-signature-seal">${
    pr.memed_signed
      ? `Assinado eletronicamente na Memed${signedAtText ? ` em ${esc(signedAtText)}` : ''}`
      : 'Emitido na Memed — conferir assinatura na receita digital'
  }</span>
</div>
${clinic.prescription_footer_text ? `<div class="prx-footer-note">${esc(clinic.prescription_footer_text)}</div>` : ''}`;

  return buildClinicPrintDocumentHtml({
    clinic,
    browserTitle: 'Receita digital Memed',
    bodyHtml,
  });
}

function buildLocalPrescriptionSignatureHtml(
  pr: any,
  clinic: any,
  issueDate?: string,
  showIssueDate = true,
): string {
  const sigImgUrl = String(pr.professional_signature_image_url ?? '').trim();
  const sigImg = sigImgUrl
    ? `<div class="clinic-doc-sign-prof-img-wrap"><img src="${esc(sigImgUrl)}" alt="" /></div>`
    : '';
  const spacerCls = sigImg ? 'clinic-doc-sign-spacer clinic-doc-sign-spacer--with-img' : 'clinic-doc-sign-spacer';
  const credentialsHtml = Array.isArray(pr.professional_credentials)
    ? pr.professional_credentials.filter(Boolean).map((c: string) => `<p class="clinic-doc-sign-caption" style="font-style:italic;color:#374151;">${esc(c)}</p>`).join('')
    : '';
  const regLine = pr.professional_registration || clinic.responsible_registration
    ? `<p class="clinic-doc-sign-caption">${esc(pr.professional_registration_type || clinic.responsible_registration_type || 'CRM')} ${esc(pr.professional_registration || clinic.responsible_registration)}${pr.professional_registration_state ? ` - ${esc(pr.professional_registration_state)}` : ''}</p>`
    : '';
  const emissionDate = formatDate(issueDate ?? pr.issue_date);
  const dateSlot = showIssueDate
    ? `<div class="clinic-doc-sign-slot clinic-doc-sign-slot--narrow">
      <div class="clinic-doc-sign-spacer"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">Data: ${emissionDate}</p>
    </div>`
    : '';

  return `<div class="clinic-doc-sign-zone">
  <div class="clinic-doc-sign-row">
    <div class="clinic-doc-sign-slot">
      ${sigImg}
      <div class="${spacerCls}"></div>
      <div class="clinic-doc-sign-rule"></div>
      <p class="clinic-doc-sign-caption">${esc(pr.professional_name || clinic.responsible_name || 'Profissional Responsável')}</p>
      ${credentialsHtml}
      ${regLine}
    </div>
    ${dateSlot}
  </div>
</div>`;
}

type LocalPrescriptionPageOptions = {
  monthIndex?: number;
  totalMonths?: number;
  monthStep?: number;
  issueDate?: string;
  validityDate?: string;
  viaLabel?: string;
  items?: any[];
  itemStartIndex?: number;
  physicalPageIndex?: number;
  physicalPageCount?: number;
};

type PrescriptionPageParts = {
  head: string;
  body: string;
  foot: string;
};

function buildLocalPrescriptionPageParts(
  pr: any,
  clinic: any,
  opts: LocalPrescriptionPageOptions = {},
): PrescriptionPageParts {
  const issueDate = opts.issueDate ?? pr.issue_date;
  const validityDate = opts.validityDate ?? pr.validity_date;
  const totalMonths = opts.totalMonths ?? 1;
  const monthIndex = opts.monthIndex ?? 0;
  const showIssueDate = !getHideIssueDateOnPrint(pr.print_settings);
  const issueLine = showIssueDate
    ? `Nº ${esc(pr.prescription_number)} | Emissão: ${formatDate(issueDate)} | Validade: ${formatDate(validityDate)}`
    : `Nº ${esc(pr.prescription_number)}`;

  const items = opts.items ?? (Array.isArray(pr.items) ? pr.items : []);
  const pt = resolveEffectivePrescriptionType(pr.prescription_type, items);
  const titleBlock =
    pt === 'controlled_blue' || pt === 'special_control' || pt === 'dental'
      ? 'RECEITUÁRIO DE CONTROLE ESPECIAL'
      : pt === 'controlled_yellow'
        ? 'NOTIFICAÇÃO DE RECEITA A'
        : pt === 'special_white'
          ? 'NOTIFICAÇÃO DE RECEITA ESPECIAL'
          : pt === 'antimicrobial'
            ? 'RECEITUÁRIO - ANTIMICROBIANO'
            : 'RECEITUÁRIO';

  const medsHtml = buildMedicationsHtml(items, opts.itemStartIndex ?? 0);
  const tc = prescriptionTypeColors[pt] || prescriptionTypeColors.special_control;

  const monthBanner =
    totalMonths > 1
      ? `<div class="prx-month-banner">${(opts.monthStep ?? 1) > 1 ? 'Folha' : 'Mês'} ${monthIndex + 1} de ${totalMonths} — Competência: ${esc(formatMonthCompetence(issueDate))}</div>`
      : '';
  const viaBanner = opts.viaLabel
    ? `<div class="prx-via-banner">${esc(opts.viaLabel)}</div>`
    : '';
  const pageCounter = (opts.physicalPageCount ?? 1) > 1
    ? `<div class="prx-page-counter">Página ${(opts.physicalPageIndex ?? 0) + 1} de ${opts.physicalPageCount}</div>`
    : '';

  const head = `${monthBanner}
${viaBanner}
${pageCounter}
${buildPrescriptionHeaderBlockHtml(clinic)}
<div class="prx-title">${titleBlock}</div>
<div class="prx-type-badge">
  <span style="background-color: ${tc}20; color: ${tc};">${esc(prescriptionTypeLabel(pt))}</span>
</div>
<div class="prx-number">
  ${issueLine}
</div>
${buildPatientSectionHtml(pr)}
${buildClinicalInfoSectionHtml(pr)}`;

  const body = `<div class="section">
  <div class="section-title">Prescrição</div>
  ${pr.format === 'free_text'
    ? `<div class="clinical-body" style="font-size:${inlineDocumentFontSize(12)};line-height:1.7;color:#1f2937;">${renderClinicalBodyForPrint(pr.body_text)}</div>`
    : (medsHtml || `<p style="font-size:${inlineDocumentFontSize(11)};color:#9ca3af">(sem itens)</p>`)}
</div>
${pr.notes ? `<div class="section"><div class="section-title">Observações</div><p style="font-size: ${inlineDocumentFontSize(11)}; line-height: 1.6;">${esc(pr.notes)}</p></div>` : ''}
${prescriptionHasContinuousUse(pr) ? '<div class="prx-continuous-use">USO CONTÍNUO</div>' : ''}`;

  const validityFoot = showIssueDate
    ? `<div class="prx-validity">Receituário válido até ${formatDate(validityDate)}</div>`
    : '';
  const foot = `${validityFoot}
${buildLocalPrescriptionSignatureHtml(pr, clinic, issueDate, showIssueDate)}
${clinic.prescription_footer_text ? `<div class="prx-footer-note">${esc(clinic.prescription_footer_text)}</div>` : ''}`;

  return { head, body, foot };
}

/** Folha com thead/tfoot: timbre e rodapé repetem em cada página física da via. */
function wrapPrescriptionPrintSheet(clinic: any, parts: PrescriptionPageParts): string {
  const letterhead = isClinicLetterheadEnabled(clinic)
    ? `<div class="clinic-sheet-letterhead">${buildClinicLetterheadBlockHtml(clinic)}</div>`
    : buildLetterheadTopSpacerHtml(clinic);
  const footerUnits = buildLetterheadFooterUnitsHtml(clinic);

  return `<table class="prx-print-table">
  <thead>
    <tr><td class="prx-print-head">${letterhead}${parts.head}</td></tr>
  </thead>
  <tfoot>
    <tr><td class="prx-print-foot">${footerUnits}</td></tr>
  </tfoot>
  <tbody>
    <tr><td class="prx-print-body">${parts.body}${parts.foot}</td></tr>
  </tbody>
</table>`;
}

/** Há algum bloco com mais de uma via (controlado, antimicrobiano…) — só então faz sentido «apenas 1ª via». */
export function prescriptionHasMultipleVias(pr: any): boolean {
  if (!pr || pr.origin === 'memed') return false;
  return splitPrescriptionByViaRequirement(pr).some((block) => block.viaCount > 1);
}

export type PrescriptionPrintOptions = {
  /** Imprime só a 1ª via de cada bloco/mês (as demais vias são omitidas). */
  firstViaOnly?: boolean;
};

export type PrescriptionPrintPayload = { prescription: any; clinic: any };

/** Folhas A4 de um receituário (local; Memed usa HTML próprio). */
export function buildPrescriptionPrintSheets(
  printData: PrescriptionPrintPayload,
  options: PrescriptionPrintOptions = {},
): { clinic: any; sheets: ClinicPrintSheetSpec[]; browserTitle: string } {
  const printDataSafe = printData || { prescription: {}, clinic: {} };
  const pr = printDataSafe.prescription || {};
  const clinic = printDataSafe.clinic || {};

  const multiCount = getMultiMonthCount(pr.print_settings);
  const multiStep = getMultiMonthStep(pr.print_settings);
  const portraitCss =
    '@page { size: A4 portrait; } @media print { .prx-sheet-portrait { max-width: 180mm; margin: 0 auto; } }';
  const blocks = splitPrescriptionByViaRequirement(pr);
  const sheets: ClinicPrintSheetSpec[] = [];

  for (let i = 0; i < multiCount; i++) {
    const dates = shiftPrescriptionDates(pr.issue_date, pr.validity_date, i * multiStep);
    blocks.forEach((block) => {
      const blockPrescription = { ...pr, prescription_type: block.prescriptionType, items: block.items };
      const viaLabels = getPrescriptionViaLabels(block.viaCount);
      const itemPages = pr.format === 'free_text'
        ? [{ items: [] as any[], startIndex: 0 }]
        : paginatePrescriptionItems(block.items);

      const viasToPrint = options.firstViaOnly ? Math.min(1, block.viaCount) : block.viaCount;
      for (let v = 0; v < viasToPrint; v++) {
        itemPages.forEach((itemPage, physicalPageIndex) => {
          const parts = buildLocalPrescriptionPageParts(blockPrescription, clinic, {
            monthIndex: i,
            totalMonths: multiCount,
            monthStep: multiStep,
            issueDate: dates.issueDate,
            validityDate: dates.validityDate,
            viaLabel: viaLabels[v] || undefined,
            items: itemPage.items,
            itemStartIndex: itemPage.startIndex,
            physicalPageIndex,
            physicalPageCount: itemPages.length,
          });
          const sheetInner = wrapPrescriptionPrintSheet(clinic, parts);
          const wrapped = `<div class="prx-sheet-portrait">${sheetInner}</div>`;
          sheets.push({
            bodyHtml: `<style>${PRESCRIPTION_EXTRA_CSS}${portraitCss}</style>${wrapped}`,
            selfContainedLetterhead: true,
          });
        });
      }
    });
  }

  const browserTitle = multiCount > 1 ? `Receituário (${multiCount} meses)` : 'Receituário';
  return { clinic, sheets, browserTitle };
}

/** Várias receitas separadas em um único HTML (cada receita em nova página). */
export function buildMultiplePrescriptionsPrintHtml(
  printDatas: PrescriptionPrintPayload[],
  options: PrescriptionPrintOptions = {},
): string {
  const items = (printDatas || []).filter((p) => p?.prescription);
  if (!items.length) {
    return buildClinicPrintDocumentHtml({
      clinic: null,
      browserTitle: 'Receituário',
      bodyHtml: '',
    });
  }
  if (items.length === 1 && items[0].prescription?.origin === 'memed') {
    return buildMemedPrescriptionPrintHtml(items[0].prescription, items[0].clinic || {});
  }
  if (items.length === 1) {
    return buildPrescriptionPrintHtml(items[0], options);
  }

  const allSheets: ClinicPrintSheetSpec[] = [];
  let clinic = items[0].clinic || {};
  for (const item of items) {
    if (item.prescription?.origin === 'memed') {
      const memedHtml = buildMemedPrescriptionPrintHtml(item.prescription, item.clinic || clinic);
      allSheets.push({ bodyHtml: memedHtml, selfContainedLetterhead: true });
      continue;
    }
    const built = buildPrescriptionPrintSheets(item, options);
    clinic = built.clinic || item.clinic || clinic;
    allSheets.push(...built.sheets);
  }

  if (!allSheets.length) {
    return buildClinicPrintDocumentHtml({ clinic, browserTitle: 'Receituários', bodyHtml: '' });
  }

  return buildClinicMultiSheetPrintHtml({
    clinic,
    browserTitle: `Receituários (${items.length})`,
    sheets: allSheets,
  });
}

export function buildPrescriptionPrintHtml(
  printData: PrescriptionPrintPayload,
  options: PrescriptionPrintOptions = {},
): string {
  const printDataSafe = printData || { prescription: {}, clinic: {} };
  const pr = printDataSafe.prescription || {};
  const clinic = printDataSafe.clinic || {};

  if (pr.origin === 'memed') return buildMemedPrescriptionPrintHtml(pr, clinic);

  const { sheets, browserTitle } = buildPrescriptionPrintSheets(printDataSafe, options);

  if (!sheets.length) {
    return buildClinicPrintDocumentHtml({
      clinic,
      browserTitle: 'Receituário',
      bodyHtml: '',
    });
  }

  return buildClinicMultiSheetPrintHtml({
    clinic,
    browserTitle,
    sheets,
  });
}

export function prescriptionPrintQueueLabel(printData: PrescriptionPrintPayload): string {
  const pr = printData?.prescription || {};
  const num = String(pr.prescription_number || '').trim();
  const patient = String(pr.patient_name || '').trim();
  if (num && patient) return `${num} — ${patient}`;
  return num || patient || 'Receituário';
}
